import { Button } from '@/components/ui/button';
import { AutocompleteInput } from '@/components/forms/AutocompleteInput';
import { FormInput } from '@/components/ui/form-input';
import { FormItem, FormMessage, FormField } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useHybridForm } from '@/hooks/useHybridForm';
import { useCustomerNameAutocomplete } from '../hooks/useCustomerNameAutocomplete';
import { useForm, useStore } from '@tanstack/react-form';
import React from 'react';
import { FormProvider } from 'react-hook-form';
import {
  formValidationSchema,
  vinSchema,
  type FormValues,
} from '../utils/validation';
import type { AutocompleteSuggestion, NameType } from '../types';

export type SearchMode = 'vinAndName' | 'contractNumber';

// This interface defines the actual structure of data submitted by the form
interface ContractSearchFormSubmitValues {
  searchMode: SearchMode;
  vin?: string;
  customerName?: string;
  contractNumber?: string;
  /**
   * Optional nameType from autocomplete selection.
   * When user selects from autocomplete, this indicates which field matched:
   * - 'buyer': customer name (first + last or just last)
   * - 'business': business name
   * - 'cobuyer': cobuyer name
   *
   * When user types manually without selecting from autocomplete, this is undefined.
   */
  nameType?: NameType;
}

interface ContractSearchFormProps {
  onSubmit: (values: ContractSearchFormSubmitValues) => void;
  isSubmitting: boolean;
  onSearchModeChange?: (searchMode: SearchMode) => void;
}

// Extract defaultValues to a shared constant
const FORM_DEFAULT_VALUES: FormValues = {
  searchMode: 'vinAndName' as const,
  vin: '',
  customerName: '',
  contractNumber: '',
};

/**
 * Custom renderer for contract autocomplete suggestions
 */
function renderContractSuggestion(
  suggestion: AutocompleteSuggestion,
  query: string
) {
  // If no query, just display the text without highlighting
  if (!query.trim()) {
    return (
      <div className='flex items-center justify-between w-full'>
        <span>{suggestion.suggestion}</span>
        <span className='text-xs text-muted-foreground ml-2'>
          ({suggestion.nameType})
        </span>
      </div>
    );
  }

  // Create a regex to find matches, escaping special regex characters in the query
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = suggestion.suggestion.split(regex);

  return (
    <div className='flex items-center justify-between w-full'>
      <span>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span
              key={i}
              className='bg-primary/10 text-primary font-medium px-0.5 rounded-sm'
            >
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </span>
      <span className='text-xs text-muted-foreground ml-2'>
        ({suggestion.nameType})
      </span>
    </div>
  );
}

export function ContractSearchForm({
  onSubmit,
  isSubmitting,
  onSearchModeChange,
}: ContractSearchFormProps) {
  const [currentSearchMode, setCurrentSearchMode] =
    React.useState<SearchMode>('vinAndName');

  // Track the nameType from autocomplete selection for precise backend filtering.
  // When user types manually without selecting from autocomplete, this is undefined.
  const [selectedNameType, setSelectedNameType] = React.useState<
    NameType | undefined
  >(undefined);

  // Track when an autocomplete selection is in progress to prevent onChange from clearing nameType.
  // When form.setFieldValue is called, it triggers the field's onChange which would clear nameType.
  const isSelectingFromAutocomplete = React.useRef<boolean>(false);

  // Track the last VIN for which we fetched all suggestions to avoid duplicate calls
  const lastVinWithSuggestions = React.useRef<string>('');

  const form = useForm({
    defaultValues: FORM_DEFAULT_VALUES,
    onSubmit: async ({ value }) => {
      const submissionData: ContractSearchFormSubmitValues = {
        searchMode: value.searchMode,
      };
      if (value.searchMode === 'vinAndName') {
        submissionData.vin = value.vin;
        submissionData.customerName = value.customerName;
        // Include nameType if user selected from autocomplete
        submissionData.nameType = selectedNameType;
      } else if (value.searchMode === 'contractNumber') {
        submissionData.contractNumber = value.contractNumber;
      }
      onSubmit(submissionData);
    },
    validators: {
      onSubmit: formValidationSchema,
    },
  });

  const rhfForm = useHybridForm(
    form,
    Object.keys(FORM_DEFAULT_VALUES) as (keyof FormValues)[],
    FORM_DEFAULT_VALUES
  );

  // Subscribe to specific form state properties to ensure proper reactivity
  // This is the recommended approach from TanStack Form documentation to avoid
  // unnecessary re-renders while ensuring the button state updates correctly
  const { isValid, isSubmitting: formIsSubmitting } = useStore(
    form.store,
    (state) => ({
      isValid: state.isValid,
      isSubmitting: state.isSubmitting,
    })
  );

  // Autocomplete logic for customer name
  const autocomplete = useCustomerNameAutocomplete({
    vin: form.getFieldValue('vin') || '',
    onSuggestionSelect: (suggestion: AutocompleteSuggestion) => {
      // Set flag to prevent onChange from clearing nameType
      isSelectingFromAutocomplete.current = true;
      form.setFieldValue('customerName', suggestion.suggestion);
      // Track the nameType for pre-selecting the dropdown option
      setSelectedNameType(suggestion.nameType);
      void form.validateField('customerName', 'change');
      // Reset flag after React's batched updates complete
      queueMicrotask(() => {
        isSelectingFromAutocomplete.current = false;
      });
    },
    isVinValid: () => {
      const vinValue = form.getFieldValue('vin') || '';
      return vinSchema.safeParse(vinValue).success;
    },
  });

  /**
   * Handles VIN field blur event to automatically load customer suggestions
   * if VIN is valid and no suggestions are currently loaded.
   */
  const handleVinBlur = React.useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      const vinValue = e.target.value;

      // Only proceed if we're in VIN & Name mode
      if (currentSearchMode !== 'vinAndName') {
        return;
      }

      // Use the autocomplete's VIN validation and check if suggestions aren't already loaded
      if (
        autocomplete.suggestions.length === 0 &&
        lastVinWithSuggestions.current !== vinValue
      ) {
        // Fetch all suggestions if VIN is valid (validation happens inside fetchAllSuggestions)
        await autocomplete.fetchAllSuggestions();

        // Only update tracking if suggestions were actually fetched (VIN was valid)
        const isValid = vinSchema.safeParse(vinValue).success;
        if (isValid) {
          lastVinWithSuggestions.current = vinValue;
        }
      }
    },
    [currentSearchMode, autocomplete]
  );

  const handleSearchModeChange = async (value: string) => {
    const newMode = value as SearchMode;
    setCurrentSearchMode(newMode);

    // Notify parent of search mode change
    onSearchModeChange?.(newMode);

    // Update search mode field and validate it
    await form.setFieldValue('searchMode', newMode);
    await form.validateField('searchMode', 'change');

    // Clear fields from other mode and suggestions
    if (newMode === 'vinAndName') {
      await form.setFieldValue('contractNumber', '');
      await form.validateField('contractNumber', 'change');
    } else {
      await form.setFieldValue('vin', '');
      await form.setFieldValue('customerName', '');
      await form.validateField('vin', 'change');
      await form.validateField('customerName', 'change');
    }
    autocomplete.clearSuggestions();
    // Clear nameType when switching modes
    setSelectedNameType(undefined);

    // Reset the VIN suggestion tracking when switching modes
    lastVinWithSuggestions.current = '';

    // Final validation of all fields
    await form.validateAllFields('change');
  };

  return (
    <FormProvider {...rhfForm}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void form.handleSubmit();
        }}
        className='space-y-6'
      >
        {/* Search Mode Selection */}
        <FormItem className='space-y-3'>
          <Label className='text-sm font-medium'>Search by:</Label>
          <RadioGroup
            onValueChange={handleSearchModeChange}
            value={currentSearchMode}
            className='flex flex-col space-y-1 md:flex-row md:space-x-4 md:space-y-0'
          >
            <FormItem className='flex items-center space-x-3 space-y-0'>
              <RadioGroupItem value='vinAndName' id='searchModeVinName' />
              <Label htmlFor='searchModeVinName' className='font-normal'>
                VIN & Name
              </Label>
            </FormItem>
            <FormItem className='flex items-center space-x-3 space-y-0'>
              <RadioGroupItem value='contractNumber' id='searchModeContract' />
              <Label htmlFor='searchModeContract' className='font-normal'>
                Contract Number
              </Label>
            </FormItem>
          </RadioGroup>
          <form.Field name='searchMode'>{() => <FormMessage />}</form.Field>
        </FormItem>

        {/* Conditional Fields */}
        {currentSearchMode === 'vinAndName' && (
          <>
            <form.Field name='vin'>
              {(field) => (
                <FormField
                  control={rhfForm.control}
                  name='vin'
                  render={() => (
                    <FormInput
                      field={field}
                      label='VIN'
                      placeholder='Enter VIN'
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        autocomplete.clearSuggestions();
                        // Reset suggestion tracking when VIN changes
                        lastVinWithSuggestions.current = '';
                        // Clear nameType when VIN changes - previous selection is no longer valid
                        setSelectedNameType(undefined);
                      }}
                      onBlur={handleVinBlur}
                    />
                  )}
                />
              )}
            </form.Field>

            <form.Field name='customerName'>
              {(field) => (
                <FormField
                  control={rhfForm.control}
                  name='customerName'
                  render={() => (
                    <AutocompleteInput
                      name={field.name}
                      label='Customer Name'
                      value={field.state.value ?? ''}
                      onChange={(value) => {
                        field.handleChange(value);
                        autocomplete.handleInputChange(value);
                        // Clear nameType when user manually types - they may be changing the selection
                        // Skip clearing if this change was triggered by autocomplete selection
                        if (!isSelectingFromAutocomplete.current) {
                          setSelectedNameType(undefined);
                        }
                      }}
                      onBlur={(e) => {
                        field.handleBlur();
                        autocomplete.handleInputBlur(e.target.value);
                      }}
                      onFocus={(e) => {
                        void autocomplete.handleInputFocus(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        autocomplete.handleKeyDown(e, e.currentTarget.value);
                      }}
                      placeholder='Enter customer name'
                      description='Full name or last name.'
                      suggestions={autocomplete.suggestions}
                      isFetching={autocomplete.isFetching}
                      focusedIndex={autocomplete.focusedIndex}
                      onSuggestionClick={autocomplete.handleSuggestionClick}
                      renderSuggestion={renderContractSuggestion}
                      minMatchingLength={3}
                    />
                  )}
                />
              )}
            </form.Field>
          </>
        )}

        {currentSearchMode === 'contractNumber' && (
          <form.Field name='contractNumber'>
            {(field) => (
              <FormField
                control={rhfForm.control}
                name='contractNumber'
                render={() => (
                  <FormInput
                    field={field}
                    label='Contract Number'
                    placeholder='Enter contract number'
                  />
                )}
              />
            )}
          </form.Field>
        )}

        <Button
          type='submit'
          disabled={isSubmitting || !isValid || formIsSubmitting}
        >
          {isSubmitting || formIsSubmitting ? 'Searching...' : 'Search'}
        </Button>
      </form>
    </FormProvider>
  );
}
